export interface RenderRecovery<T, R> {
  rendered: { items: T[]; value: R }[];
  failed: T[];
}

/**
 * Try all items once, splitting only failed batches. Each item is indivisible.
 * Rendering and yielding are injected; this function owns no editor, cache,
 * drawing, or logging. Successful batches and failed items retain input order.
 */
export async function renderWithRetries<T, R>(
  items: T[],
  render: (items: T[]) => Promise<R | null>,
  beforeRetry?: () => Promise<void>,
): Promise<RenderRecovery<T, R>> {
  if (items.length === 0) return { rendered: [], failed: [] };
  let value: R | null = null;
  try {
    value = await render(items);
  } catch {
    // A rejected export and an explicit null have the same recovery path.
  }
  if (value !== null) return { rendered: [{ items, value }], failed: [] };
  if (items.length === 1) return { rendered: [], failed: [...items] };

  const mid = Math.ceil(items.length / 2);
  await beforeRetry?.();
  const left = await renderWithRetries(
    items.slice(0, mid),
    render,
    beforeRetry,
  );
  await beforeRetry?.();
  const right = await renderWithRetries(items.slice(mid), render, beforeRetry);
  return {
    rendered: [...left.rendered, ...right.rendered],
    failed: [...left.failed, ...right.failed],
  };
}
